import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, RefreshCw, Mail, Trash2, Smartphone, MessageSquare, Send } from "lucide-react";
import { AdminComposer } from "@/components/site/AdminComposer";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ATT_BLUE, ATT_LOGOS } from "@/attData";
import { LOGO_URL } from "@/data";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["New", "Contacted", "Ordered", "Closed"];

const inputCls =
  "w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 font-body text-sm text-white placeholder:text-white/30 focus:border-[#00A8E0] focus:outline-none";

const statusColor = { New: "#00A8E0", Contacted: "#d4af37", Ordered: "#34d399", Closed: "#9aa4b0" };

export const AdminHeader = ({ title, subtitle }) => (
  <div className="pt-32 pb-10 bg-[#06111f] border-b border-white/10">
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <div className="flex flex-wrap items-center gap-4">
        <img src={LOGO_URL} alt="Heritage Family Protection Agency" className="h-12 w-auto object-contain" />
        <div className="inline-flex items-center rounded-xl bg-white overflow-hidden">
          <img src="/att/att-authorized-dealer.jpg" alt="AT&T Authorized Dealer" className="h-12 w-auto object-contain" />
        </div>
      </div>
      <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-white">
        {title} <span className="italic" style={{ color: ATT_BLUE }}>{subtitle}</span>
      </h1>
      <div className="mt-4 flex flex-wrap gap-4 font-body text-sm">
        <Link to="/admin/att" className="text-white/60 hover:text-[#00A8E0] transition-colors" data-testid="admin-nav-att">AT&T Sales</Link>
        <Link to="/admin/applicants" className="text-white/60 hover:text-[#00A8E0] transition-colors" data-testid="admin-nav-applicants">Applicants</Link>
        <Link to="/admin/quotes" className="text-white/60 hover:text-[#d4af37] transition-colors" data-testid="admin-nav-quotes">Client Inbox</Link>
      </div>
    </div>
  </div>
);

export const AdminGate = ({ code, setCode, onSubmit, error, loading }) => (
  <form onSubmit={onSubmit} className="max-w-md rounded-3xl border border-[#00A8E0]/30 bg-[#0a192f] p-8 space-y-4" data-testid="admin-login-form">
    <div className="flex items-center gap-2" style={{ color: ATT_BLUE }}>
      <Lock size={18} />
      <span className="font-body text-sm tracking-widest uppercase">Access Code Required</span>
    </div>
    <input
      type="password"
      data-testid="admin-code-input"
      className={inputCls}
      placeholder="Enter access code"
      value={code}
      onChange={(e) => setCode(e.target.value)}
    />
    {error && <p data-testid="admin-error" className="font-body text-sm text-red-400">{error}</p>}
    <button
      type="submit"
      disabled={loading || !code}
      data-testid="admin-login-btn"
      className="btn-sweep w-full rounded-full px-8 py-3.5 font-body text-sm font-semibold tracking-wide text-white disabled:opacity-60"
      style={{ backgroundColor: ATT_BLUE }}
    >
      {loading ? "Checking..." : "Unlock"}
    </button>
  </form>
);

const Field = ({ label, value }) => (
  <div>
    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/35">{label}</p>
    <p className="font-body text-sm text-white/85 break-words">{value || "—"}</p>
  </div>
);

const SMSComposer = ({ sms, onClose, code }) => {
  const [phone, setPhone] = useState(sms.to_phone || "");
  const [message, setMessage] = useState(`Hi ${(sms.to_name || "").split(" ")[0]}, this is Heritage Family Protection Agency (AT&T Authorized Dealer) about your pre-order. `);
  const [sending, setSending] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/api/att/send-sms`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Code": code },
        body: JSON.stringify({ to_phone: phone, message, to_name: sms.to_name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Text failed to send.");
      toast.success(`Text sent to ${phone}`);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="sms-composer" className="max-w-md border-white/15 bg-[#0a192f] p-8">
        <DialogTitle className="font-serif-display text-2xl font-medium text-white flex items-center gap-3">
          <MessageSquare size={20} style={{ color: ATT_BLUE }} /> Text Customer
        </DialogTitle>
        <form onSubmit={send} className="mt-4 space-y-4">
          <input data-testid="sms-phone-input" className={inputCls} placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <textarea data-testid="sms-message-input" rows={5} maxLength={640} className={inputCls} placeholder="Your text message" value={message} onChange={(e) => setMessage(e.target.value)} />
          <p className="font-body text-[11px] text-white/35">{message.length}/640 · Sent from your toll-free business number.</p>
          <button
            type="submit"
            disabled={sending}
            data-testid="sms-send-btn"
            className="btn-sweep w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 font-body text-sm font-semibold tracking-wide text-white disabled:opacity-60"
            style={{ backgroundColor: ATT_BLUE }}
          >
            {sending ? "Sending..." : "Send Text"} <Send size={15} />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminATTPage() {
  const [code, setCode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("residential");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [compose, setCompose] = useState(null);
  const [sms, setSms] = useState(null);

  const load = async (e, accessCode = code) => {
    e && e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/att/preorders`, { headers: { "X-Admin-Code": accessCode } });
      if (res.status === 401) { setError("Invalid access code."); setAuthed(false); return; }
      if (!res.ok) throw new Error();
      setOrders(await res.json());
      setAuthed(true);
    } catch {
      setError("Could not load data. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    const prev = orders.find((x) => x.id === id)?.status;
    setOrders((o) => o.map((x) => (x.id === id ? { ...x, status } : x)));
    const res = await fetch(`${API}/api/att/preorders/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-Admin-Code": code },
      body: JSON.stringify({ status }),
    });
    if (res.ok) toast.success(`Order marked ${status}`);
    else {
      setOrders((o) => o.map((x) => (x.id === id ? { ...x, status: prev } : x)));
      toast.error("Couldn't update status.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this pre-order permanently?")) return;
    const res = await fetch(`${API}/api/att/preorders/${id}`, { method: "DELETE", headers: { "X-Admin-Code": code } });
    if (res.ok) {
      setOrders((o) => o.filter((x) => x.id !== id));
      toast.success("Pre-order deleted.");
    } else toast.error("Couldn't delete. Try again.");
  };

  const shown = orders.filter((o) => filter === "all" || o.audience === filter);

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="admin-att-page">
      <AdminHeader title="AT&T Sales" subtitle="back office." />
      <section className="relative py-10 pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {!authed ? (
            <AdminGate code={code} setCode={setCode} onSubmit={load} error={error} loading={loading} />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div className="flex flex-wrap gap-2">
                  {[["residential", `Residential Orders (${orders.filter((o) => o.audience === "residential").length})`], ["business", `Business Orders (${orders.filter((o) => o.audience === "business").length})`]].map(([v, label]) => (
                    <button
                      key={v}
                      onClick={() => setFilter(v)}
                      data-testid={`att-admin-filter-${v}`}
                      className={`rounded-full px-5 py-2.5 font-body text-sm tracking-wide transition-colors duration-300 ${filter === v ? "text-white font-semibold" : "border border-white/15 text-white/60 hover:text-white"}`}
                      style={filter === v ? { backgroundColor: ATT_BLUE } : {}}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button onClick={() => load()} data-testid="att-admin-refresh" className="inline-flex items-center gap-2 font-body text-sm hover:opacity-80 transition-opacity" style={{ color: ATT_BLUE }}>
                  <RefreshCw size={15} /> Refresh
                </button>
              </div>

              {shown.length === 0 ? (
                <p className="font-body text-white/50" data-testid="att-admin-empty">No pre-orders here yet.</p>
              ) : (
                <div className="space-y-4">
                  {shown.map((o) => (
                    <div key={o.id} data-testid={`att-admin-order-${o.id}`} className="rounded-2xl border border-white/10 bg-[#0a192f] p-6">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 className="font-serif-display text-2xl text-white flex items-center gap-3">
                          <Smartphone size={20} style={{ color: ATT_BLUE }} />
                          {o.name}
                          <span className="rounded-full px-3 py-0.5 font-body text-[10px] font-bold uppercase tracking-widest text-black" style={{ backgroundColor: o.audience === "business" ? "#fcf6ba" : "#7dd3fc" }}>
                            {o.audience}
                          </span>
                        </h3>
                        <div className="flex items-center gap-3">
                          <span className="font-body text-xs text-white/40">{new Date(o.created_at).toLocaleString()}</span>
                          <select
                            data-testid={`att-admin-status-${o.id}`}
                            value={o.status || "New"}
                            onChange={(e) => updateStatus(o.id, e.target.value)}
                            className="rounded-full border border-white/20 bg-black/50 px-3 py-1.5 font-body text-xs font-bold uppercase tracking-wider focus:outline-none"
                            style={{ color: statusColor[o.status || "New"] }}
                          >
                            {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0a192f] text-white">{s}</option>)}
                          </select>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
                        <Field label="Email" value={<a href={`mailto:${o.email}`} className="hover:text-[#00A8E0]">{o.email}</a>} />
                        <Field label="Phone" value={<a href={`tel:${o.phone}`} className="hover:text-[#00A8E0]">{o.phone}</a>} />
                        <Field label="Business name" value={o.business_name} />
                        <Field label="Lines" value={o.lines} />
                        <Field label="Hero 20% discount" value={o.hero_discount && o.hero_discount !== "No" ? `${o.hero_discount} — verify ID` : o.hero_discount} />
                        <Field label="Porting number?" value={o.porting} />
                        <Field label="Current carrier" value={o.current_carrier} />
                        <Field label="Account number" value={o.account_number} />
                        <Field label="Transfer PIN" value={o.transfer_pin} />
                        <Field label="Trading in a phone?" value={o.trade_in} />
                        <Field label="Trade-in phone" value={o.trade_in_phone} />
                        <Field label="Trade-in storage" value={o.trade_in_storage} />
                        <Field label="Trade-in condition" value={o.trade_in_condition} />
                        <Field label="Trade-in carrier" value={o.trade_in_carrier} />
                        <Field label="Trade-in IMEI" value={o.trade_in_imei} />
                        <Field label="Wireless plan" value={o.wireless_plan || o.plan} />
                        <Field label="Internet plan" value={o.internet_plan} />
                        <Field label="Order ID" value={o.id.slice(0, 8)} />
                      </div>

                      <div className="mt-4">
                        <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/35">Devices requested</p>
                        {o.devices && o.devices.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {o.devices.map((d, i) => (
                              <span key={i} className="rounded-full border border-[#00A8E0]/40 bg-[#00A8E0]/10 px-3 py-1 font-body text-xs text-[#7dd3fc]">
                                {d.qty}× {d.name} ({d.category})
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="font-body text-sm text-white/40 mt-1">No devices selected</p>
                        )}
                      </div>

                      {o.configuration && (
                        <div className="mt-4 rounded-xl border border-[#00A8E0]/30 bg-[#00A8E0]/5 p-4" data-testid={`att-admin-config-${o.id}`}>
                          <p className="font-body text-[10px] uppercase tracking-[0.2em] text-[#7dd3fc]">Configured build</p>
                          <div className="mt-2 space-y-0.5">
                            {o.configuration.split("\n").map((line, i) => (
                              <p key={i} className={`font-body ${i === 0 ? "text-sm font-bold text-white" : "text-xs text-white/60"}`}>{line}</p>
                            ))}
                          </div>
                          {(o.est_monthly || o.est_onetime) && (
                            <p className="mt-2 font-body text-xs font-bold text-[#7dd3fc]">
                              Est. {o.est_monthly || "—"} monthly · {o.est_onetime || "—"} one-time
                            </p>
                          )}
                        </div>
                      )}

                      {o.notes && <p className="mt-3 font-body text-sm text-white/55 italic">"{o.notes}"</p>}

                      <div className="mt-5 flex flex-wrap gap-3">
                        <button
                          onClick={() => setCompose({ template: "att", to_email: o.email, to_name: o.name, subject: `Your AT&T ${o.audience} pre-order — Heritage Family Protection Agency`, message: "" })}
                          data-testid={`att-admin-email-${o.id}`}
                          className="inline-flex items-center gap-2 rounded-full px-5 py-2 font-body text-xs tracking-widest uppercase text-white font-semibold"
                          style={{ backgroundColor: ATT_BLUE }}
                        >
                          <Mail size={13} /> Email Customer
                        </button>
                        <button
                          onClick={() => setSms({ to_phone: o.phone, to_name: o.name })}
                          data-testid={`att-admin-sms-${o.id}`}
                          className="inline-flex items-center gap-2 rounded-full border px-5 py-2 font-body text-xs tracking-widest uppercase font-semibold transition-colors hover:bg-white/5"
                          style={{ borderColor: "rgba(0,168,224,0.5)", color: ATT_BLUE }}
                        >
                          <MessageSquare size={13} /> Text Customer
                        </button>
                        <button
                          onClick={() => remove(o.id)}
                          data-testid={`att-admin-delete-${o.id}`}
                          className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {compose && <AdminComposer open onClose={() => setCompose(null)} code={code} initial={compose} />}
              {sms && <SMSComposer sms={sms} onClose={() => setSms(null)} code={code} />}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
